import React, { useId } from 'react';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { Input, Label, Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/fluent-adapter';
import { Alert, AlertDescription } from '@/fluent-adapter';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';
import { Warning24Regular as AlertTriangle, Info24Regular as InfoIcon } from '@fluentui/react-icons';
import type { TypeOfRequestForm } from '../../store/types';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import { allowsDateOverride } from '../../utils/requestParametersHelpers';

interface DateOverrideData {
  requestStartDate: string | null;
  requestEndDate: string | null;
}

interface LegalOrderDetailsStepProps {
  typeOfRequestData: TypeOfRequestForm;
  onTypeOfRequestChange: (data: Partial<TypeOfRequestForm>) => void;
  errors?: Record<string, string>;
  isUSA?: boolean;
  onDateOverride?: (data: DateOverrideData) => void;
  currentDates?: DateOverrideData;
}

const US_REQUEST_TYPES = [
  { value: 'Subpoena or Summons', label: 'Subpoena or Summons' },
  { value: 'Court Order', label: 'Court Order' },
  { value: 'Search Warrant', label: 'Search Warrant' },
  { value: 'PR/TT', label: 'PR/TT (Pen Register / Trap and Trace)' },
  { value: 'Lawful Intercept', label: 'Lawful Intercept' },
  { value: 'Emergency Letter', label: 'Emergency Letter' },
  { value: 'Other', label: 'Other' },
];

const REQUEST_TYPES_REQUIRING_SUBTYPE = ['PR/TT', 'Lawful Intercept'];

const REQUEST_SUBTYPES = [
  { value: 'Renewal', label: 'Renewal' },
  { value: 'Start', label: 'Start' },
  { value: 'Stop', label: 'Stop' },
];

const LEGAL_ORDER_TYPES = [
  { value: 'Microsoft Corporation', label: 'Microsoft Corporation' },
  { value: 'Microsoft Ireland Operations Limited', label: 'Microsoft Ireland Operations Limited' },
  { value: 'Other', label: 'Other Microsoft Entity' },
];

const requiresSubtype = (requestType: string | null): boolean => {
  if (!requestType) return false;
  return REQUEST_TYPES_REQUIRING_SUBTYPE.includes(requestType);
};

const isEmergencyType = (requestType: string | null): boolean => {
  if (!requestType) return false;
  return requestType.toLowerCase().includes('emergency');
};

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXXL },
  header: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  title: { fontSize: '20px', fontWeight: 600 },
  muted: { color: tokens.colorNeutralForeground3 },
  errorText: { color: tokens.colorStatusDangerForeground1 },
  sectionTitle: { fontSize: '16px', fontWeight: 600 },
  sectionContent: { ...shorthands.padding('0px') },
  formField: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
  dateRangeInputs: {
    display: 'flex',
    gap: tokens.spacingHorizontalXL,
    flexWrap: 'wrap',
    width: '100%',
  },
  dateField: {
    flex: '1 1 auto',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground4,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  required: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
});

export const LegalOrderDetailsStep: React.FC<LegalOrderDetailsStepProps> = ({
  typeOfRequestData,
  onTypeOfRequestChange,
  errors = {},
  isUSA = true,
  onDateOverride,
  currentDates,
}) => {
  const styles = useStyles();
  const legalOrderTypeLabelId = useId();
  const requestTypeLabelId = useId();
  const requestSubTypeLabelId = useId();
  const isPreservation = typeOfRequestData.isPreservationRequest === true;
  const showDateOverride = allowsDateOverride(typeOfRequestData.requestType) && onDateOverride;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.title}>Legal Order Details</h2>
        <p className={styles.muted}>
          Provide details about your legal order and request type
        </p>
      </div>

      {!isUSA && (
        <Card>
          <CardHeader>
            <CardTitle className={styles.sectionTitle}>Legal Order Type</CardTitle>
            <CardDescription>
              Who should the legal order be addressed to?
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
            <div className={styles.formField}>
              <Label id={legalOrderTypeLabelId}>Legal Order Addressed To<RequiredIndicator /></Label>
              <Select
                aria-labelledby={legalOrderTypeLabelId}
                value={typeOfRequestData.legalOrderType || ''}
                onValueChange={(value) => onTypeOfRequestChange({ legalOrderType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {LEGAL_ORDER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.legalOrderType && (
                <p className={styles.errorText}>{errors.legalOrderType}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!isPreservation && (
        <Card>
          <CardHeader>
            <CardTitle className={styles.sectionTitle}>Request Type</CardTitle>
            <CardDescription>
              Select the type of legal process you are submitting
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
            <div className={styles.formField}>
              <Label id={requestTypeLabelId}>Request Type<RequiredIndicator /></Label>
              <Select
                aria-labelledby={requestTypeLabelId}
                value={typeOfRequestData.requestType || ''}
                onValueChange={(value) => {
                  onTypeOfRequestChange({ requestType: value, requestSubType: null });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  {US_REQUEST_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.requestType && (
                <p className={styles.errorText}>{errors.requestType}</p>
              )}
            </div>

            {requiresSubtype(typeOfRequestData.requestType) && (
              <div className={styles.formField}>
                <Label id={requestSubTypeLabelId}>Request Sub-Type<RequiredIndicator /></Label>
                <Select
                  aria-labelledby={requestSubTypeLabelId}
                  value={typeOfRequestData.requestSubType || ''}
                  onValueChange={(value) => onTypeOfRequestChange({ requestSubType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sub-type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUEST_SUBTYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.requestSubType && (
                  <p className={styles.errorText}>{errors.requestSubType}</p>
                )}
              </div>
            )}

            {isEmergencyType(typeOfRequestData.requestType) && (
              <Alert>
                <AlertTriangle />
                <AlertDescription>
                  You have selected &apos;{typeOfRequestData.requestType}&apos; as the request type.
                  Emergencies are for immediate, threat to life issues only.
                  Once you have submitted, please email your LNS number to lealert@microsoft.com.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className={styles.sectionTitle}>Law Enforcement Reference Number</CardTitle>
          <CardDescription>
            Enter your internal case or reference number for tracking purposes
          </CardDescription>
        </CardHeader>
        <CardContent className={styles.cardContent}>
          <div className={styles.formField}>
            <Label htmlFor="leReferenceNumber">Law Enforcement Reference Number<RequiredIndicator /></Label>
            <Input
              id="leReferenceNumber"
              value={typeOfRequestData.leReferenceNumber || ''}
              placeholder="Enter your reference number"
              onChange={(e) => onTypeOfRequestChange({ leReferenceNumber: e.target.value })}
              maxLength={FIELD_LIMITS.LE_REFERENCE_NUMBER}
            />
            {errors.leReferenceNumber && (
              <p className={styles.errorText}>{errors.leReferenceNumber}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {showDateOverride && (
        <Card>
          <CardHeader>
            <CardTitle className={styles.sectionTitle}>Date Range Override</CardTitle>
            <CardDescription>
              Specify the date range for this {typeOfRequestData.requestType} request
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
            <div className={styles.infoBox}>
              <InfoIcon />
              <span>For {typeOfRequestData.requestType} requests, you may specify a date range that extends beyond today. These dates will override the date range from the previous step.</span>
            </div>
            <div className={styles.dateRangeInputs}>
              <div className={styles.dateField}>
                <Label htmlFor="overrideStartDate">Start Date<RequiredIndicator /></Label>
                <Input
                  id="overrideStartDate"
                  type="date"
                  value={currentDates?.requestStartDate ?? ''}
                  onChange={(e) => onDateOverride({ requestStartDate: e.target.value || null, requestEndDate: currentDates?.requestEndDate ?? null })}
                />
              </div>
              <div className={styles.dateField}>
                <Label htmlFor="overrideEndDate">End Date<RequiredIndicator /></Label>
                <Input
                  id="overrideEndDate"
                  type="date"
                  value={currentDates?.requestEndDate ?? ''}
                  onChange={(e) => onDateOverride({ requestStartDate: currentDates?.requestStartDate ?? null, requestEndDate: e.target.value || null })}
                />
              </div>
            </div>
            {errors.requestDateRange && (
              <p className={styles.errorText}>{errors.requestDateRange}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
